/**
 * Tag normalization utilities for questionnaires/surveys.
 *
 * Tags are stored as a comma-separated string in FHIR extensions. These utilities
 * provide consistent normalization across the application to prevent state
 * divergence between client and server.
 */

/**
 * Normalizes a single tag: trims whitespace and removes commas.
 *
 * Commas must be stripped because tags are stored as a comma-separated string.
 * A tag containing commas would corrupt data when read back via split(",").
 * Casing is preserved as typed (e.g. "URGENT" stays "URGENT") - see `normalizeTags`
 * for how duplicates that differ only by case are resolved.
 *
 * @param tag - The raw tag string to normalize
 * @returns The normalized tag (trimmed, no commas, original casing preserved)
 *
 * @example
 * normalizeTag("  Hello World  ") // "Hello World"
 * normalizeTag("foo,bar") // "foobar"
 * normalizeTag("  URGENT  ") // "URGENT"
 */
export function normalizeTag(tag: string): string {
  return tag.trim().replace(/,/g, "");
}

/**
 * Normalizes an array of tags: normalizes each tag, removes empty/duplicate tags, and sorts.
 *
 * Duplicates are matched case-insensitively (so "Urgent" and "urgent" collapse to one
 * tag) but the casing of the first occurrence is preserved rather than forced to
 * lowercase - a tag typed in UPPERCASE (e.g. an acronym) stays UPPERCASE.
 *
 * Sorting ensures consistent ordering between client and server state, preventing
 * unnecessary "dirty" flags due to array ordering differences.
 *
 * @param tags - Array of raw tag strings
 * @returns Normalized, deduplicated, and sorted array of tags
 *
 * @example
 * normalizeTags(["  Urgent ", "follow-up", "URGENT"]) // ["follow-up", "Urgent"]
 * normalizeTags(["b", "a", "c"]) // ["a", "b", "c"]
 * normalizeTags(["", "  ", "valid"]) // ["valid"]
 */
export function normalizeTags(tags: string[]): string[] {
  const firstOccurrenceByKey = new Map<string, string>();
  for (const tag of tags) {
    const normalized = normalizeTag(tag);
    if (!normalized) continue;
    const key = normalized.toLocaleLowerCase();
    if (!firstOccurrenceByKey.has(key)) {
      firstOccurrenceByKey.set(key, normalized);
    }
  }
  return [...firstOccurrenceByKey.values()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
}

/**
 * Parses a comma-separated tags string into an array of normalized tags.
 *
 * This is the inverse of `tagsToString()` and is used when reading tags
 * from FHIR extensions.
 *
 * @param tagsString - Comma-separated tags string (or undefined/empty)
 * @returns Array of normalized tags
 *
 * @example
 * parseTags("foo,bar,baz") // ["bar", "baz", "foo"]
 * parseTags("  urgent , follow-up ") // ["follow-up", "urgent"]
 * parseTags("") // []
 * parseTags(undefined) // []
 */
export function parseTags(tagsString: string | undefined | null): string[] {
  if (!tagsString) return [];
  return normalizeTags(tagsString.split(","));
}

/**
 * Converts an array of tags to a comma-separated string for storage.
 *
 * Tags are normalized before joining. Returns undefined if the resulting
 * string would be empty (for clean FHIR extension handling).
 *
 * @param tags - Array of tags to convert
 * @returns Comma-separated string of normalized tags, or undefined if empty
 *
 * @example
 * tagsToString(["urgent", "follow-up"]) // "follow-up,urgent"
 * tagsToString([]) // undefined
 * tagsToString(["", "  "]) // undefined
 */
export function tagsToString(tags: string[]): string | undefined {
  const normalized = normalizeTags(tags);
  return normalized.length > 0 ? normalized.join(",") : undefined;
}
